import React, { useState, useRef } from 'react';
import { compressImage } from '../utils/imageCompression';
import { XIcon, UploadIcon, CameraIcon } from 'lucide-react';
interface PhotoUploadSectionProps {
  title: string;
  description: string;
  section: string;
  onUpload: (base64Images: string[]) => void;
  required: boolean;
}
export function PhotoUploadSection({
  title,
  description,
  section,
  onUpload,
  required
}: PhotoUploadSectionProps) {
  const [image, setImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setIsUploading(true);
    try {
      const file = e.target.files[0];
      const compressedImage = await compressImage(file);
      setImage(compressedImage);
      onUpload([compressedImage]); // Still send as array for compatibility
    } catch (error) {
      console.error('Error compressing image:', error);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const removeImage = () => {
    setImage(null);
    onUpload([]);
  };
  return <div className="border border-gray-200 rounded-lg p-3 bg-white bg-opacity-50 shadow-sm" data-id="element-95">
      <div className="flex justify-between items-start mb-1" data-id="element-96">
        <div data-id="element-97">
          <h3 className="font-medium text-[#052858] text-sm sm:text-base" data-id="element-98">
            {title} {required && <span className="text-red-500" data-id="element-99">*</span>}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500" data-id="element-100">{description}</p>
        </div>
        <div className="flex items-center" data-id="element-101">
          <span className="text-xs sm:text-sm text-gray-500 mr-2" data-id="element-102">
            {image ? '1 foto' : "0 foto's"}
          </span>
        </div>
      </div>
      <div className="mt-2" data-id="element-103">
        {image ? <div className="relative aspect-[4/3] w-full max-h-[200px] overflow-hidden rounded-md" data-id="element-104">
            <img src={image} alt={`Uploaded ${title}`} className="w-full h-full object-contain rounded-md" data-id="element-105" />
            <button type="button" onClick={removeImage} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors" data-id="element-106">
              <XIcon size={16} data-id="element-107" />
            </button>
          </div> : <label className="border-2 border-dashed border-[#19EAC4] rounded-md flex flex-col items-center justify-center cursor-pointer aspect-[4/3] hover:bg-[#C8FAF1] hover:bg-opacity-20 transition-colors p-4" data-id="element-108">
            <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileChange} className="sr-only" data-id="element-109" />
            {isUploading ? <div className="animate-pulse text-center" data-id="element-110">
                <div className="w-8 h-8 mx-auto mb-2 border-2 border-[#19EAC4] border-t-transparent rounded-full animate-spin" data-id="element-111"></div>
                <span className="text-xs text-gray-600" data-id="element-112">Uploading...</span>
              </div> : <>
                <div className="w-10 h-10 mb-2 rounded-full bg-[#C8FAF1] flex items-center justify-center" data-id="element-113">
                  <CameraIcon size={18} className="text-[#052858]" data-id="element-114" />
                </div>
                <span className="text-xs text-center text-[#052858] font-medium" data-id="element-115">
                  Klik om een foto toe te voegen
                </span>
                <span className="text-xs text-center text-gray-500 mt-1" data-id="element-116">
                  Maximaal 1 foto
                </span>
              </>}
          </label>}
      </div>
    </div>;
}